import { useContext, useEffect, useRef } from 'react';
import {
  DEALER_PORTRAIT_HEIGHT,
  DEALER_PORTRAIT_WIDTH,
  POV_DEALER_TOP_PX,
  GAME_HAND_PHASE,
  SEAT_NAME_ABSOLUTE_MIN_PX,
  SEAT_NAME_MIN_PAINTED_PX,
  SEAT_STACK_MIN_FONT_PX,
  SEAT_STACK_MIN_PAINTED_PX,
  SEAT_TEXT_MIN_FONT_PX,
  TRAY_OPEN_CARD_LIFT_MS
} from 'constants/gameConstants';
import { DealerAnimationContext } from 'contexts/DealerAnimationContext';
import DealerChipContext from 'contexts/DealerChipContext';
import GameContext from 'contexts/GameContext';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import PovCardsContext from 'contexts/PovCardsContext';
import { useShowAllDealers } from 'contexts/ShowAllDealersContext';
import { TableIdContext } from 'contexts/TableIdContext';
import { useAnimatedWinStack } from 'hooks/useAnimatedWinStack';
import { useDisplayedStack } from 'hooks/useDisplayedStack';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { useRegisterFloatingAnchor } from 'hooks/useFloatingRegistry';
import { useTableSelector } from 'hooks/useTableSelector';
import { useTrayClearanceLift } from 'hooks/useTrayClearanceLift';
import { TableZ } from 'styles/zIndex';
import {
  isHighWinner,
  isLowWinner,
  shouldDimCard,
  tableMoneyFormatterCompact
} from 'utils/functions';

import HandRank from 'components/HandRank/HandRank';
import { DealerChip, PovCards } from 'components/Seat/components';
import { SeatActionEffects } from 'components/Seat/components/SeatAction';
import { useShrinkToFit } from 'components/Seat/hooks/useShrinkToFit';
import { SeatWithPlayer } from 'components/Seat/SeatWithPlayer';
import { AmountWon } from 'components/Seat/SeatWithPlayer/AmountWon';
import { BountyBullets } from 'components/Seat/SeatWithPlayer/BountyBullets';
import { WinnerMsg } from 'components/Seat/SeatWithPlayer/WinnerMsg';
import {
  actionBadgeStyle,
  bountyBadgePositionStyle,
  cardOutlineStyle,
  myPlayerInfoCol,
  myPlayerName,
  myPlayerRowStyle,
  myPlayerStack,
  remToPx
} from 'components/Seat/styles';

import { getPortraitModeStyles, useSeatAnimation } from './index';
import { isLivePovCardObservation } from './povCardVisibility';

type PortraitCurrentPlayerProps = {
  seat: any;
  seatId: number;
  currentPlayerSeatId: number;
  isFaceCardUp: boolean;
  position: SeatPosition;
  cards: any;
  hasAction: boolean;
  playerAction: string;
  displayAction?: string;
  actionColor: any;
  isWinner: boolean;
  isTurn: boolean;
  cardPosition: any;
  holeCardCount: number;
  isRotating?: boolean;
};

export const PortraitCurrentPlayer = ({
  seat,
  seatId,
  currentPlayerSeatId,
  isFaceCardUp,
  position,
  cards,
  hasAction,
  playerAction,
  displayAction,
  actionColor,
  isWinner,
  isTurn,
  cardPosition,
  holeCardCount,
  isRotating = false
}: PortraitCurrentPlayerProps) => {
  const game = useTableSelector((table) => table.game);
  // Which table this cue belongs to — keys the cross-table win dedup.
  const winCueTableId = useContext(TableIdContext);
  const gameState = useTableSelector((table) => table.gameState);
  const isPlayerFolded = useTableSelector((table) => table.currentPlayer.isFolded);
  const isPlayerJoined = useTableSelector((table) => table.currentPlayer.isJoined);
  const isPlayerWaiting = useTableSelector((table) => table.currentPlayer.isWaiting);
  const isPlayerWaitingAndExit = useTableSelector((table) => table.currentPlayer.isWaitingAndExit);
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const { isAnimating, dealer } = useContext(DealerAnimationContext);
  const { isBetTrayOpen } = useContext(GameContext);
  const displayedStack = useAnimatedWinStack(seatId, useDisplayedStack(seatId, seat.stack));

  // The name and the balance MEASURE themselves and shrink to fit the row,
  // exactly as the opponent rows do (SeatCardPill). Predicting the size from
  // `--seat-scale` instead grew the layout font as the table shrank, inside a
  // row whose width did not grow with it — which is what truncated the POV name
  // in mobile portrait and multi-table grid view. See myPlayerName.
  const stackText =
    displayedStack !== undefined ? tableMoneyFormatterCompact(displayedStack, game.tabletype) : '';
  const nameRef = useShrinkToFit<HTMLSpanElement>(
    seat.displayName,
    remToPx('0.9rem'),
    SEAT_NAME_ABSOLUTE_MIN_PX,
    // START at 10 painted px, so a scaled-down seat does not paint the design
    // size far smaller than intended…
    SEAT_TEXT_MIN_FONT_PX,
    // …but SHRINK past it rather than holding it and truncating. A name is an
    // identity: "Connsa…" no longer tells you who you are playing.
    SEAT_NAME_MIN_PAINTED_PX
  );
  const stackRef = useShrinkToFit<HTMLSpanElement>(
    stackText,
    remToPx('0.88rem'),
    SEAT_STACK_MIN_PAINTED_PX,
    // START at 8 painted px, then SHRINK past it rather than truncating: a
    // truncated money figure is misleading, not merely shortened.
    SEAT_STACK_MIN_FONT_PX,
    SEAT_STACK_MIN_PAINTED_PX
  );

  const seatRef = useRef<HTMLDivElement>(null);
  const turnGlowRef = useRef<HTMLDivElement>(null);
  const opponentGlowRef = useRef<HTMLDivElement>(null);
  const dealerChipRef = useRef<HTMLDivElement>(null);
  const handNum = Number(game.hand);
  const hasActiveHandPhase = Number.isFinite(handNum);
  // Presentation gate only: these statuses should not show local cards until the
  // server moves the player into an active hand, matching Unity's UIPlayer flow.
  const isWaitingForHand = isPlayerJoined || isPlayerWaiting || isPlayerWaitingAndExit;
  const hasSeenPreDealRef = useRef(
    !hasActiveHandPhase || handNum < GAME_HAND_PHASE.PRE_FLOP_BETTING_ROUND
  );
  // Tracks whether this UI actually displayed POV cards in the hand; prevents
  // end-of-hand collect animation from rendering stale setup/join cards.
  const hasSeenPovCardsRef = useRef(false);

  const showAllDealers = useShowAllDealers();
  const showDealerChip =
    showAllDealers || (!!game.dealerVisible && seatId === dealer && !isAnimating);

  useRegisterFloatingAnchor(DealerChipContext, seatId, dealerChipRef);
  // EN-10420: publish the hole-cards element so the POV bet pill (Table/Bet)
  // can park itself a fixed gap above the LIFTED cards while the tray is open.
  const povCardsRef = useRef<HTMLDivElement>(null);
  useRegisterFloatingAnchor(PovCardsContext, seatId, povCardsRef);
  // Travel measured against the tray's real top edge — see the hook.
  const trayLiftTransform = useTrayClearanceLift(isBetTrayOpen, povCardsRef);

  useSeatAnimation(isWinner, isTurn, true, seatRef, turnGlowRef, opponentGlowRef);
  const { getMyPlayerWrapperStyle, getMyPlayerContentStyle } = getPortraitModeStyles(
    position,
    isRotating
  );

  const showCards = game.seatsWithVisibleCards?.includes(seatId);
  useEffect(() => {
    if (
      gameState.preDeal ||
      !hasActiveHandPhase ||
      handNum < GAME_HAND_PHASE.PRE_FLOP_BETTING_ROUND
    ) {
      hasSeenPreDealRef.current = true;
    }
  }, [gameState.preDeal, handNum, hasActiveHandPhase]);

  const hasCards = cards?.some((card: string) => !!card);
  useEffect(() => {
    if (isLivePovCardObservation({ showCards, hasActiveHandPhase, handNum, isWaitingForHand })) {
      hasSeenPovCardsRef.current = true;
    }

    if (!hasCards || isWaitingForHand) {
      hasSeenPovCardsRef.current = false;
    }
  }, [handNum, hasActiveHandPhase, hasCards, isWaitingForHand, showCards]);

  const isShowCardDecision =
    !!gameState.pokerActions.showCard ||
    !!gameState.pokerActions.muckCard ||
    gameState.startMucking ||
    game.seatsWithShowCard?.includes(seatId) ||
    game.seatsWithMuckCard?.includes(seatId);
  const hasShownCards = game.seatsWithShowCard?.includes(seatId);
  const shouldForcePovFaceDown = !showCards && isShowCardDecision && !hasShownCards;
  // Unity returns cards on RecordStats/AddOnsAndCharging. This is animation
  // visibility state, not poker rule evaluation.
  const shouldCollectPovCards =
    !isHandHistorySnapshot &&
    hasCards &&
    hasActiveHandPhase &&
    handNum >= GAME_HAND_PHASE.RECORD_NEW_STATS_AND_START_NEW_HAND &&
    hasSeenPovCardsRef.current &&
    !isWaitingForHand;
  // The deal window is hand 4 (DEAL_CARDS), when every seat is dealt at once:
  // this seat gets face-down placeholders there and turns them up when the
  // real cards arrive at hand 5 (EN-10985). Hand 5 (PRE_FLOP_BETTING_ROUND,
  // Unity's "DealCards") stays in the window for a client that never saw 4.
  const shouldAnimatePovDeal =
    !isHandHistorySnapshot &&
    hasSeenPreDealRef.current &&
    showCards &&
    hasActiveHandPhase &&
    (handNum === GAME_HAND_PHASE.DEAL_CARDS ||
      handNum === GAME_HAND_PHASE.PRE_FLOP_BETTING_ROUND) &&
    !isWaitingForHand;
  const showPovCards = isHandHistorySnapshot
    ? showCards || hasCards
    : !isWaitingForHand &&
      (showCards || shouldCollectPovCards || ((isPlayerFolded || isShowCardDecision) && hasCards));

  const isHigh = isHighWinner(game.pots, game.showdown, seatId);
  const isLow = isLowWinner(game.pots, game.showdown, seatId);
  const showHighWinner = isHigh;
  const showLowWinner = isLow && game.languageKey !== 'GAME_MSG_ALLFOLD';

  return (
    <div style={getMyPlayerWrapperStyle()}>
      <div ref={seatRef} style={getMyPlayerContentStyle()}>
        {/* WinnerMsg above the cards (portrait current player only) */}
        {(showHighWinner || showLowWinner) && (
          <div style={{ position: 'relative', height: 0 }}>
            {/* isOwnWin is unconditionally true here: this component IS the POV
                seat, which is why SeatWithPlayer opts out of rendering the
                message for it. Winning is the end of the player's own turn, so
                it crosses tables like the fold and the countdown — go all in,
                switch away to wait it out, and the hand resolving in your
                favour still reaches you (EN-10585 AC1). */}
            {showLowWinner ? (
              <WinnerMsg
                backgroundColor='#628B48'
                message='Low Winner'
                isOwnWin
                gameID={String(winCueTableId)}
              />
            ) : (
              <WinnerMsg backgroundColor='#628B48' isOwnWin gameID={String(winCueTableId)} />
            )}
          </div>
        )}

        {/* Row 1: the POV DECISION GROUP — hole cards with the hand-strength
            label locked to them, 4px apart.
            EN-10420: one container, one transform. While the tray is open the
            GROUP glides up by exactly the overlap the tray actually creates,
            plus a fixed gap — measured, not predicted, so the gap is identical
            on every device (see hooks/useTrayClearanceLift). The label always
            travels with the cards it describes, so no dead felt opens between
            them: the "extra empty space below the POV cards" QA reported (Jira
            66026) was a lift that ignored how much the tray actually covered.
            povCardsRef sits HERE (the group's top edge IS the cards' top edge),
            so the bet pill keeps measuring the same anchor. */}
        <div
          ref={povCardsRef}
          data-pov-decision-group
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            // The group paints ABOVE the dealer button (a later sibling that
            // would otherwise win by DOM order) — and stays above it while it
            // travels through the button's zone.
            position: 'relative',
            zIndex: 2,
            transform: trayLiftTransform,
            transition: `transform ${TRAY_OPEN_CARD_LIFT_MS}ms ease-out`
          }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            {showPovCards ? (
              <PovCards
                cards={cards}
                isFaceDown={shouldForcePovFaceDown}
                isDimmed={(card) => isPlayerFolded || shouldDimCard(game, card)}
                shouldAnimateDeal={shouldAnimatePovDeal}
                shouldCollect={shouldCollectPovCards}
              />
            ) : (
              <div style={{ display: 'flex', gap: '4px' }}>
                {Array.from({ length: holeCardCount }).map((_, i) => (
                  <div key={i} style={cardOutlineStyle} />
                ))}
              </div>
            )}
          </div>
          {isFaceCardUp && <HandRank />}
        </div>

        {/* Row 2: Avatar + name + balance + action badge.
            EN-10420 decision (Jason, product/design 8/12): this row does NOT
            ride with the cards while the bet tray is open — the tray covers it
            intentionally. The decision info a player needs mid-bet is the
            CARDS and the bet pill (both lift), plus the countdown, which the
            tray duplicates internally (TrayCountdownRing). Lifting the stack
            row too pulled the whole cluster up the felt and crowded the board;
            a player sizing a bet reads their stack off the tray's All In
            preset / slider max instead. Supersedes the original "stack stays
            visible" reading of the EN-10420 AC — see the ticket note. */}
        <div
          ref={turnGlowRef}
          style={{
            ...myPlayerRowStyle,
            position: 'relative',
            borderRadius: isTurn || isWinner ? '999px' : (myPlayerRowStyle as any).borderRadius,
            ...(isWinner
              ? {
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  borderColor: 'rgba(98, 139, 72, 0.8)',
                  backgroundColor: 'rgba(98, 139, 72, 0.15)'
                }
              : {}),
            ...(actionColor && !isWinner
              ? {
                  backgroundColor: actionColor.bg,
                  borderColor: actionColor.border
                }
              : {})
          }}>
          <BountyBullets
            totalBounty={Number(seat.totalBounty) || 0}
            bountyAmount={Number(game.bountyAmount) || 0}
            gameType={game.gameType}
            style={bountyBadgePositionStyle}
          />
          {hasAction && (
            <span style={{ ...actionBadgeStyle, backgroundColor: actionColor?.border }}>
              {displayAction ?? playerAction.toUpperCase()}
            </span>
          )}
          <div
            style={{
              transform: 'scale(0.5)',
              transformOrigin: 'center center',
              flexShrink: 0,
              margin: '-14px',
              position: 'relative'
            }}>
            {/* Gated with the POV's action badge above — see SeatCardPill for
                why this unmounts rather than hides. */}
            {hasAction && <SeatActionEffects action={playerAction} />}
            <SeatWithPlayer
              {...seat}
              currentPlayerSeatId={currentPlayerSeatId}
              seatId={seatId}
              action={playerAction}
              isFaceCardUp={isFaceCardUp}
              cardPosition={cardPosition}
              hideInfo
              hideWinnerMsg
            />
          </div>
          <div style={myPlayerInfoCol}>
            {/* No fontSize on either row — useShrinkToFit owns it, and React
                would reset the fitted size on the next stack tick. */}
            <span ref={nameRef} style={myPlayerName}>
              {seat.displayName}
            </span>
            <span ref={stackRef} style={myPlayerStack}>
              {stackText}
            </span>
          </div>
          <AmountWon seatId={seatId} />
        </div>

        {/* Dealer button — centered above the POV player's card band, i.e.
          toward the table center, clear of the avatar. The POV seat is the one
          exception to getSeatDealerOffset's inside-the-card-bottom rule: this
          seat is bottom-centre, so "toward the table centre" is UP, and its own
          hole cards occupy the right-hand side of the row. */}
        <div
          ref={dealerChipRef}
          style={{
            position: 'absolute',
            // Under this seat's own hole cards — see styles/zIndex.ts.
            zIndex: TableZ.withinSeatDealer,
            top: POV_DEALER_TOP_PX,
            left: '50%',
            transform: 'translate(-50%, -100%)',
            width: DEALER_PORTRAIT_WIDTH,
            height: DEALER_PORTRAIT_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: showDealerChip ? 1 : 0,
            pointerEvents: showDealerChip ? 'auto' : 'none',
            borderRadius: '50%'
          }}>
          <DealerChip isPortrait />
        </div>
      </div>
    </div>
  );
};
